import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useIdleTimer — auto-logout on inactivity (Vague 97.2 — security hardening).
 *
 * Tracks user activity (mouse, keyboard, touch, scroll) and exposes:
 *   • `phase`  : 'active' | 'warning' | 'expired'
 *   • `remaining` : seconds left before auto-logout (only meaningful in 'warning')
 *   • `reset()`   : manually reset the timer (e.g. when user clicks "Stay logged in")
 *
 * Cross-tab synchronisation: any tab seeing real activity broadcasts an event so
 * other tabs reset their timer in lockstep. This avoids the surprise of "I was
 * working in tab A but tab B logged me out".
 */

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
const SYNC_KEY = 'ifaap_idle_last_active';
const SYNC_THROTTLE_MS = 5_000; // only broadcast cross-tab activity every 5s

export function useIdleTimer({ idleMinutes = 30, warnSeconds = 60, onTimeout, enabled = true }) {
  const [phase, setPhase] = useState('active');
  const [remaining, setRemaining] = useState(warnSeconds);

  const idleMs = idleMinutes * 60_000;
  const warnMs = warnSeconds * 1_000;

  const lastActivityRef = useRef(Date.now());
  const lastBroadcastRef = useRef(0);
  const warnTimerRef = useRef(null);
  const expireTimerRef = useRef(null);
  const tickerRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);

  // Keep the latest onTimeout without re-arming timers on every render.
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  const clearTimers = () => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (expireTimerRef.current) { clearTimeout(expireTimerRef.current); expireTimerRef.current = null; }
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
  };

  const armTimers = useCallback(() => {
    clearTimers();
    setPhase('active');
    setRemaining(warnSeconds);

    warnTimerRef.current = setTimeout(() => {
      setPhase('warning');
      setRemaining(warnSeconds);
      // Tick the countdown once per second for the modal.
      tickerRef.current = setInterval(() => {
        const elapsedSinceWarn = Date.now() - (lastActivityRef.current + (idleMs - warnMs));
        const left = Math.max(0, Math.ceil((warnMs - elapsedSinceWarn) / 1000));
        setRemaining(left);
      }, 1_000);
    }, idleMs - warnMs);

    expireTimerRef.current = setTimeout(() => {
      clearTimers();
      setPhase('expired');
      onTimeoutRef.current?.();
    }, idleMs);
  }, [idleMs, warnMs, warnSeconds]);

  const reset = useCallback(() => {
    if (!enabled) return;
    lastActivityRef.current = Date.now();
    armTimers();
    // Broadcast to other tabs (throttled).
    const now = Date.now();
    if (now - lastBroadcastRef.current > SYNC_THROTTLE_MS) {
      lastBroadcastRef.current = now;
      try { localStorage.setItem(SYNC_KEY, String(now)); } catch { /* noop */ }
    }
  }, [armTimers, enabled]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setPhase('active');
      return undefined;
    }
    armTimers();

    const onActivity = () => {
      // We only need a coarse-grained reset — the throttled broadcast already
      // protects against thrashing across tabs.
      const now = Date.now();
      if (now - lastActivityRef.current < 1_000) return; // sub-second noise
      lastActivityRef.current = now;
      armTimers();
      if (now - lastBroadcastRef.current > SYNC_THROTTLE_MS) {
        lastBroadcastRef.current = now;
        try { localStorage.setItem(SYNC_KEY, String(now)); } catch { /* noop */ }
      }
    };

    const onStorage = (e) => {
      // Activity in another tab — silently realign our timer (no broadcast).
      if (e.key !== SYNC_KEY || !e.newValue) return;
      lastActivityRef.current = Number(e.newValue) || Date.now();
      armTimers();
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      window.removeEventListener('storage', onStorage);
      clearTimers();
    };
  }, [armTimers, enabled]);

  return { phase, remaining, reset };
}
